<template>
    <v-row>
      <v-col cols="12" class="py-0">
        <ComponentsFixedBar :minimized-side-menu="minimizedSideMenu" :title="$t('general.documents')">
          <v-btn :color="mainColor"
                 dark
                 :fab="$vuetify.breakpoint.smAndDown"
                 :small="$vuetify.breakpoint.smAndDown"
                 :loading="loadingUploadDocument"
                 @click="openPickFileDialog()">
            <v-icon>mdi-text-box-plus</v-icon>
            <span class="hidden-sm-and-down pl-2">{{ $t('general.uploadDocument') }}</span>
          </v-btn>
          <input type="file"
                 :multiple="false"
                 ref="importerInput"
                 style="display: none"
                 @change="pickFile">
        </ComponentsFixedBar>
      </v-col>

      <v-col cols="12" style="padding-top: 50px" v-if="userDocuments.length === 0">
        <div style="width: 100%; height: 30vh" class="d-flex flex-column align-center justify-center">
          <div class="pb-2 d-flex justify-center align-center">
            <v-icon :color="mainColor" size="45px" class="mr-2">mdi-file-document</v-icon>
          </div>
          <span class="kbGray--text text-center d-block"
                style="font-size: 18px; margin: 0 auto"
                :style="{ maxWidth: $vuetify.breakpoint.smAndDown ? '100%' : '70%' }">
            {{ $t('general.noDocumentsWereFoundForThisPerson') }}
          </span>
        </div>
      </v-col>

      <v-col cols="12" style="padding-top: 50px" v-else>
        <v-list>
          <template v-for="(document, index) in userDocuments">
            <v-list-item :key="index"
                         class="grey lighten-3"
                         :class="indexToUpdateDelete !== index &&
                                 !document.description ? 'mb-2' : ''">
              <v-list-item-avatar>
                <v-icon color="kbDarkBlue">mdi-file-document</v-icon>
              </v-list-item-avatar>

              <v-list-item-content class="py-0">
                <v-list-item-title class="kbDarkBlue--text">{{ document.name }}</v-list-item-title>
              </v-list-item-content>

              <v-list-item-action class="d-flex align-center flex-row" v-if="indexToUpdateDelete === -1">
                <v-menu left>
                  <template v-slot:activator="{ on }">
                    <v-btn color="kbDarkBlue" icon v-on="on">
                      <v-icon color="kbDarkBlue">more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="downloadDocument(document)">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlue">mdi-download</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlue--text">
                        {{ $t('general.downloadDocument') }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="openDeleteUpdateDocumentConfirmation(index, 'editing')">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlue">mdi-pencil-outline</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlue--text">
                        {{ $t('general.editDocument') }}
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="openDeleteUpdateDocumentConfirmation(index, 'deleting')">
                      <v-list-item-action>
                        <v-icon color="kbDarkBlue">mdi-delete</v-icon>
                      </v-list-item-action>
                      <v-list-item-title class="kbDarkBlue--text">
                        {{ $t('general.deleteDocument') }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-list-item-action>
            </v-list-item>

            <transition name="fade" mode="out-in" :key="`delete-confirmation-${index}`">
              <template v-if="indexToUpdateDelete === index">
                <v-row align="center"
                       class="mb-2 px-3 pb-3 grey lighten-3"
                       justify="space-between"
                       no-gutters
                       v-if="deleteOrUpdateStatus === 'deleting'">
                  <v-col cols="12">
                    <span class="kbDarkBlue--text text-center d-block py-3">
                      {{ $t('general.deleteDocumentConfirmation') }}
                    </span>
                  </v-col>
                  <v-col class="d-flex justify-center align-center">
                    <div class="px-3">
                      <v-tooltip class="ml-2" bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon
                                 class="mr-2"
                                 color="kbDarkBlue"
                                 dark
                                 :loading="loadingDeleteUpdateDocument"
                                 outlined
                                 v-on="on"
                                 @click="closeDeleteUpdateDocumentConfirmation()">
                            <v-icon size="35px">mdi-close</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('general.cancel') }}</span>
                      </v-tooltip>

                      <v-tooltip class="mr-2" bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn icon
                                 color="kbDarkBlue"
                                 dark
                                 :loading="loadingDeleteUpdateDocument"
                                 v-on="on"
                                 @click="deleteDocument(document.id)">
                            <v-icon size="42px">mdi-check-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t('general.deleteDocument') }}</span>
                      </v-tooltip>
                    </div>
                  </v-col>
                </v-row>
                <v-row v-else-if="deleteOrUpdateStatus === 'editing'"
                       no-gutters
                       class="mb-2 grey lighten-3">
                  <v-col cols="12">
                    <v-textarea color="kbDarkBlue"
                                class="pa-3"
                                hide-details
                                :label="$t('general.insertDocumentDescription')"
                                solo
                                v-model="document.description"/>
                  </v-col>
                  <v-col cols="12">
                    <div class="d-flex justify-end pa-3">
                      <v-btn class="mr-1"
                             color="kbRed"
                             outlined
                             small
                             @click="closeDeleteUpdateDocumentConfirmation(index)">{{ $t('general.cancel') }}</v-btn>
                      <v-btn :color="mainColor"
                             dark
                             :loading="loadingDeleteUpdateDocument"
                             small
                             @click="updateDocument(document)">{{ $t('general.editDescription') }}</v-btn>
                    </div>
                  </v-col>
                </v-row>
              </template>
            </transition>
            <v-row v-if="document.description && (!deleteOrUpdateStatus || indexToUpdateDelete !== index)"
                   no-gutters
                   class="grey lighten-3 mb-2"
                   :key="`document-description-${index}`">
              <v-col cols="12" class="pb-3">
                <span class="kbDarkBlue--text px-6 d-block">{{ $t('general.description') }}:</span>
                <span class="kbDarkBlue--text font-weight-light px-6">
                  {{ document.description }}
                </span>
              </v-col>
            </v-row>
          </template>
        </v-list>
      </v-col>
    </v-row>
</template>

<script>
import notifications from '@/libs/notifications'
import { mapState, mapGetters } from 'vuex'
import ComponentsFixedBar from '@/pages/App/People/dialogs/ViewUser/ComponentsFixedBar'
export default {
  components: { ComponentsFixedBar },
  computed: {
    ...mapGetters(['appUuid', 'companyUuid', 'userDocuments']),
    ...mapState({
      selectedUser: state => state.users.selectedUser
    })
  },
  created () {
    this.getUserDocuments()
  },
  data () {
    return {
      deleteOrUpdateStatus: '',
      document: {},
      documentDescription: '',
      indexToUpdateDelete: -1,
      loadingUploadDocument: false,
      loadingDeleteUpdateDocument: false,
      notifications
    }
  },
  methods: {
    closeDeleteUpdateDocumentConfirmation (index = -1) {
      this.indexToUpdateDelete = -1
      this.deleteOrUpdateStatus = ''
      this.document = {}
      if (index !== -1) {
        this.userDocuments[index].description = this.documentDescription
      }
    },
    async deleteDocument (documentId) {
      this.loadingDeleteUpdateDocument = true
      const { userId } = this.selectedUser
      try {
        await this.$store.dispatch('deleteUserDocument', { companyId: this.companyUuid, appId: this.appUuid, userId, documentId })
        this.loadingDeleteUpdateDocument = false
        notifications.success(this.$t('general.documentSuccessfullyDeleted'))
        this.closeDeleteUpdateDocumentConfirmation()
        this.getUserDocuments()
      } catch (e) {
        this.closeDeleteUpdateDocumentConfirmation()
        notifications.error(e)
        this.loadingDeleteUpdateDocument = false
        throw e
      }
    },
    downloadDocument (doc) {
      const link = document.createElement('a')
      link.href = doc.url
      link.setAttribute('download', `${doc.name}`)
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async getUserDocuments () {
      this.loadingUploadDocument = true
      try {
        const { userId } = this.selectedUser
        await this.$store.dispatch('getUserDocuments', { companyId: this.companyUuid, appId: this.appUuid, userId })
        this.loadingUploadDocument = false
      } catch (e) {
        this.loadingUploadDocument = false
        notifications.error(e)
        throw e
      }
    },
    openDeleteUpdateDocumentConfirmation (index, action) {
      this.deleteOrUpdateStatus = action
      this.indexToUpdateDelete = index
      this.documentDescription = this.userDocuments[index].description
    },
    openPickFileDialog () {
      if (this.loadingUploadDocument) return
      this.$refs.importerInput.click()
    },
    pickFile (e) {
      this.document = e.target.files[0]
      this.uploadDocument()
    },
    async updateDocument (doc) {
      if (!doc || typeof doc !== 'object' || !doc.name) {
        this.closeDeleteUpdateDocumentConfirmation()
        return notifications.error(this.$t('general.youMustPickADocumentToEdit'))
      }
      this.loadingDeleteUpdateDocument = true
      try {
        const { userId } = this.selectedUser
        const { description, id } = doc
        const data = {
          description,
          userId,
          id
        }
        await this.$store.dispatch('updateUserDocument', {
          companyId: this.companyUuid,
          appId: this.appUuid,
          data
        })
        this.loadingDeleteUpdateDocument = false
        this.closeDeleteUpdateDocumentConfirmation()
        notifications.success(this.$t('general.documentSuccessfullyEdited'))
        this.getUserDocuments()
      } catch (e) {
        notifications.error(e)
        this.loadingDeleteUpdateDocument = false
        throw e
      }
    },
    async uploadDocument () {
      if (!this.document ||
          typeof this.document !== 'object' ||
          !this.document.name) return
      const documentsNames = [...this.userDocuments].map(item => item.name.toLowerCase())
      if (documentsNames.includes(this.document.name.toLowerCase())) {
        this.document = {}
        // TODO: Translate this notification
        return notifications.error('Ya existe un documento con este nombre')
      }
      if (this.document.size / 1000000 > 10) {
        this.document = {}
        return notifications.error(this.$t('general.fileSizeMustBeUnderXSize', { size: '10Mb' }))
      }
      this.loadingUploadDocument = true
      try {
        const { userId } = this.selectedUser
        const data = {
          file: this.document,
          type: 'users'
        }
        await this.$store.dispatch('uploadUserDocument', {
          companyId: this.companyUuid,
          appId: this.appUuid,
          userId,
          data
        })
        this.document = {}
        notifications.success(this.$t('general.documentSuccessfullyUploaded'))
        await this.getUserDocuments()
        this.loadingUploadDocument = false
      } catch (e) {
        this.document = {}
        this.loadingUploadDocument = false
        notifications.error(e)
        throw e
      }
    }
  },
  props: {
    mainColor: {
      type: String
    },
    minimizedSideMenu: {
      type: Boolean,
      required: true
    }
  }
}
</script>
